import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { LANGUAGES } from "../i18n";

const initial = {
  first_name: "", last_name: "", business_name: "", email: "", phone: "", city: "",
  business_type: "", message: "", growth_goal: "", consent_accepted: false, honeypot: "",
};

const BUSINESS_TYPES = ["restaurant", "cafe", "retail", "gym", "beauty", "services", "other"];

/** Public partner-interest form. Attribution metadata is captured for future VytaliLeads. */
export const PartnerForm = () => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [startedAt] = useState(() => Date.now());

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    ["first_name", "last_name", "business_name", "city"].forEach((k) => {
      if (!form[k].trim()) e[k] = t("pub.form.requiredField");
    });
    if (!form.email.trim()) e.email = t("pub.form.requiredField");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = t("pub.form.invalidEmail");
    if (!form.consent_accepted) e.consent_accepted = t("pub.form.consentRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    const params = new URLSearchParams(window.location.search);
    try {
      await api.post("/public/partner-leads", {
        ...form,
        language: i18n.language,
        page_url: window.location.href,
        landing_page: window.location.pathname,
        referrer: document.referrer || null,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        elapsed_ms: Date.now() - startedAt,
      });
      setDone(true);
      toast.success(t("pub.form.success"));
    } catch (e) {
      toast.error(apiError(e) || t("pub.form.error"));
      setErrors({ submit: t("pub.form.error") });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="partner-form-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[hsl(var(--success))]" />
        <p className="mt-4 font-display text-xl font-bold">{t("pub.form.success")}</p>
      </div>
    );
  }

  const field = (name, label, opts = {}) => (
    <div className={opts.full ? "sm:col-span-2" : ""}>
      <label className="text-sm font-semibold" htmlFor={`partner-${name}`}>{label}</label>
      {opts.textarea ? (
        <Textarea id={`partner-${name}`} data-testid={`partner-${name}-input`} rows={opts.rows || 3}
          value={form[name]} onChange={set(name)} className="mt-1.5" />
      ) : (
        <Input id={`partner-${name}`} data-testid={`partner-${name}-input`} type={opts.type || "text"}
          value={form[name]} onChange={set(name)} className="mt-1.5 h-12" />
      )}
      {errors[name] && (
        <p data-testid={`partner-${name}-error`} className="mt-1 text-xs font-semibold text-destructive">
          {errors[name]}
        </p>
      )}
    </div>
  );

  return (
    <form onSubmit={submit} noValidate data-testid="partner-form"
      className="rounded-2xl border border-border bg-card p-6 sm:p-8">
      <h3 className="font-display text-2xl font-bold">{t("pub.formTitle")}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{t("pub.formLead")}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {field("first_name", t("pub.form.firstName"))}
        {field("last_name", t("pub.form.lastName"))}
        {field("business_name", t("pub.form.business"), { full: true })}
        {field("email", t("pub.form.email"), { type: "email" })}
        {field("phone", t("pub.form.phone"), { type: "tel" })}
        {field("city", t("pub.form.city"))}
        <div>
          <label className="text-sm font-semibold" htmlFor="partner-business-type">
            {t("pub.form.businessType")}
          </label>
          <select id="partner-business-type" data-testid="partner-business_type-input"
            value={form.business_type} onChange={set("business_type")}
            className="mt-1.5 h-12 w-full rounded-xl border border-input bg-background px-3">
            <option value="">{t("common.none")}</option>
            {BUSINESS_TYPES.map((b) => (
              <option key={b} value={b}>{t(`pub.businessTypes.${b}`, b)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold" htmlFor="partner-language">
            {t("pub.form.language")}
          </label>
          <select id="partner-language" data-testid="partner-language-input" value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className="mt-1.5 h-12 w-full rounded-xl border border-input bg-background px-3">
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        {field("growth_goal", t("pub.form.goal"), { textarea: true, full: true, rows: 2 })}
        {field("message", t("pub.form.message"), { textarea: true, full: true })}
      </div>

      {/* spam trap — hidden from users */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
        data-testid="partner-honeypot" value={form.honeypot} onChange={set("honeypot")}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

      <label className="mt-5 flex gap-3 rounded-xl bg-muted p-4 text-sm">
        <Checkbox data-testid="partner-consent-checkbox" checked={form.consent_accepted}
          onCheckedChange={(v) => setForm({ ...form, consent_accepted: !!v })} className="mt-0.5" />
        <span>{t("pub.form.consent")}</span>
      </label>
      {errors.consent_accepted && (
        <p data-testid="partner-consent_accepted-error" className="mt-1 text-xs font-semibold text-destructive">
          {errors.consent_accepted}
        </p>
      )}
      {errors.submit && (
        <p data-testid="partner-form-error" className="mt-3 rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
          {errors.submit}
        </p>
      )}

      <Button type="submit" disabled={busy} data-testid="partner-submit-btn"
        className="mt-6 h-14 w-full rounded-full text-base font-bold active:scale-[0.99]">
        {busy ? t("common.sending") : t("pub.form.send")}
      </Button>
    </form>
  );
};
